import moment from 'moment';
import 'moment/locale/fr';
import { useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SideBarAdmin from '../../components/SideBarAdmin';
import { useAxios } from '../../utils/hook/useAxios';

moment.locale('fr');

/* ─────────────────────────────────────────────────────────────
   BACKEND ROUTES À AJOUTER (voir commentaires inline)
   POST   /stripe/refund          → remboursement via PaymentIntentId
   POST   /stripe/partial-refund  → remboursement partiel
   POST   /stripe/transfer-funds  → virement hôte (déjà existant)
   GET    /stripe/payment-intent/:paymentIntentId  → détails Stripe
   PUT    /paiements/:id          → mise à jour statut/notes (déjà existant)
───────────────────────────────────────────────────────────── */

const PAIEMENT_STATUS = {
  0: {
    label: 'En attente',
    bg: 'bg-warning-subtle  text-warning',
    icon: 'bi-clock-history',
    border: 'border-warning',
  },
  1: {
    label: 'Validé',
    bg: 'bg-success-subtle  text-success',
    icon: 'bi-check-circle-fill',
    border: 'border-success',
  },
  2: {
    label: 'Annulé',
    bg: 'bg-danger-subtle   text-danger',
    icon: 'bi-x-circle-fill',
    border: 'border-danger',
  },
  3: {
    label: 'Litige',
    bg: 'bg-danger-subtle   text-danger',
    icon: 'bi-exclamation-triangle-fill',
    border: 'border-danger',
  },
  4: {
    label: 'Remboursé',
    bg: 'bg-info-subtle     text-info',
    icon: 'bi-arrow-counterclockwise',
    border: 'border-info',
  },
};

const TABS = [
  { key: 'all', label: 'Tous' },
  { key: '0', label: 'En attente' },
  { key: '1', label: 'Validés' },
  { key: '3', label: 'Litiges' },
  { key: '4', label: 'Remboursés' },
];

function fmtEur(n) {
  return (parseFloat(n) || 0).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  });
}

function StripeLink({ id, type }) {
  if (!id) return <span className="text-muted">—</span>;
  const base = 'https://dashboard.stripe.com';
  const url =
    type === 'pi'
      ? `${base}/payments/${id}`
      : type === 'tr'
        ? `${base}/connect/transfers/${id}`
        : type === 're'
          ? `${base}/refunds/${id}`
          : '#';
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="font-monospace text-decoration-none d-inline-flex align-items-center gap-1"
      style={{ fontSize: 11 }}
    >
      <span className="text-truncate" style={{ maxWidth: 160 }}>
        {id}
      </span>
      <i className="bi bi-box-arrow-up-right flex-shrink-0" style={{ fontSize: 9 }}></i>
    </a>
  );
}

/* ══════════════ Panneau gestion ══════════════ */
function DisputePanel({ item, onUpdate, onClose }) {
  const axios = useAxios();
  const [note, setNote] = useState(item?.notes || '');
  const [refAmount, setRefAmount] = useState(item?.amount || '');
  const [refReason, setRefReason] = useState('requested_by_customer');
  const [loading, setLoading] = useState(false);
  const [piDetails, setPiDetails] = useState(null);

  const host = item?.Reservation?.Host;
  const guest = item?.Reservation?.Invite;
  const s = PAIEMENT_STATUS[item?.paiementStatus] ?? PAIEMENT_STATUS['0'];

  /* Charger les détails Stripe du PaymentIntent */
  useEffect(() => {
    if (!item?.Reservation.PaymentIntentId) return;
    /*
     * ROUTE BACKEND À AJOUTER :
     * GET /stripe/payment-intent/:paymentIntentId
     * → stripe.paymentIntents.retrieve(paymentIntentId)
     * → retourne { id, amount, status, charges, refunds, ... }
     */
    axios
      .get('/stripe/payment-intent/' + item.Reservation.PaymentIntentId)
      .then(({ data }) => setPiDetails(data))
      .catch(() => {});
  }, [item?.Reservation.PaymentIntentId]);

  /* Payer l'hôte (ou réessayer si status="2") */
  async function handlePay() {
    setLoading(true);
    try {
      /* Si c'est un retry, on repasse en "0" le temps de l'appel */
      if (item.paiementStatus === '2') {
        await axios.put('/paiements/' + item.id, { paiementStatus: '0' });
      }
      const resp = await axios.post('/stripe/transfer-funds', {
        email: host?.email,
        amount: item.amount,
        paymentIntentId: item.Reservation.PaymentIntentId,
        paiementId: item.id, // permet au backend d'écrire "2" en cas d'échec Stripe
      });
      await axios.put('/paiements/' + item.id, {
        payoutId: resp.data.transferId,
        transactionID: resp.data.transferId,
        paiementStatus: '1',
        paiement_method: resp.data.source_type,
      });
      toast.success('✓ Virement hôte — ' + resp.data.transferId);
      onUpdate();
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Erreur inconnue';
      toast.error('Échec du virement — ' + msg);
      /* Le backend a déjà écrit paiementStatus="2" si paiementId était fourni.
         On recharge quand même pour refléter l'état réel. */
      onUpdate();
    } finally {
      setLoading(false);
    }
  }

  /* Remboursement total ou partiel */
  async function handleRefund() {
    if (!item.Reservation.PaymentIntentId) {
      toast.error('Impossible : PaymentIntentId manquant.');
      return;
    }
    setLoading(true);
    try {
      /*
       * ROUTE BACKEND À AJOUTER :
       * POST /stripe/refund
       * Body : { paymentIntentId, amount (en centimes), reason }
       * Handler Node.js :
       *   const refund = await stripe.refunds.create({
       *     payment_intent: paymentIntentId,
       *     amount: Math.round(amount * 100),   // optionnel → total si absent
       *     reason,                             // "duplicate"|"fraudulent"|"requested_by_customer"
       *   });
       *   await Paiement.update({ paiementStatus: "4", refundId: refund.id }, { where: { id } });
       *   return res.json({ refundId: refund.id });
       */
      const resp = await axios.post('/stripe/refund', {
        paymentIntentId: item.Reservation.PaymentIntentId,
        amount: parseFloat(refAmount),
        reason: refReason,
        paiementId: item.id,
      });
      await axios.put('/paiements/' + item.id, {
        paiementStatus: '4',
        refundId: resp.data.refundId,
        notes: note,
      });
      toast.success('✓ Remboursé — ' + resp.data.refundId);
      onUpdate();
    } catch {
      toast.error('Erreur lors du remboursement');
    } finally {
      setLoading(false);
    }
  }

  /* Ouvrir litige */
  async function handleDispute() {
    setLoading(true);
    try {
      await axios.put('/paiements/' + item.id, {
        paiementStatus: '3',
        notes: note,
      });
      toast.success('Litige ouvert');
      onUpdate();
    } catch {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  }

  /* Résoudre litige */
  async function handleResolve() {
    setLoading(true);
    try {
      await axios.put('/paiements/' + item.id, {
        paiementStatus: '1',
        notes: note,
      });
      toast.success('Litige résolu — paiement validé');
      onUpdate();
    } catch {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  }

  async function saveNote() {
    try {
      await axios.put('/paiements/' + item.id, { notes: note });
      toast.success('Note sauvegardée');
    } catch {
      toast.error('Erreur');
    }
  }

  const canRefund = item ? ['0', '1', '3'].includes(item.paiementStatus) : false;
  const canPay = item?.paiementStatus === '0';
  const canRetry = item?.paiementStatus === '2'; // virement échoué → réessayer
  const canDispute = item ? ['0', '1'].includes(item.paiementStatus) : false;
  const canResolve = item?.paiementStatus === '3';

  return (
    // <div
    //   className="offcanvas offcanvas-end"
    //   tabIndex="-1"
    //   id="disputePanel"
    //   style={{ width: "min(520px, 96vw)" }}
    // >
    //   <div className="offcanvas-header border-bottom bg-body-tertiary">
    //     <div>
    //       <h6 className="mb-0 fw-bold">
    //         <i className="bi bi-shield-exclamation me-2 text-primary"></i>
    //         {item ? `Réservation #${item.reservationId}` : "Paiement"}
    //       </h6>
    //       {item && (
    //         <small className="text-muted">Paiement interne #{item.id}</small>
    //       )}
    //     </div>
    //     <button
    //       type="button"
    //       className="btn-close"
    //       data-bs-dismiss="offcanvas"
    //       onClick={onClose}
    //     />
    //   </div>

    //   <div className="offcanvas-body p-0 overflow-y-auto">
    //     {!item ? (
    //       <div className="text-center text-muted py-5">
    //         <i className="bi bi-inbox" style={{ fontSize: 36 }}></i>
    //         <p className="mt-3 mb-0">Sélectionnez un paiement</p>
    //       </div>
    //     ) : (
    //       <>
    //         {/* ── Statut + montant ── */}
    //         <div className="px-3 py-3 border-bottom">
    //           <div className="d-flex align-items-center justify-content-between mb-3">
    //             <span
    //               className={`badge rounded-pill px-3 py-2 ${s.bg}`}
    //               style={{ fontSize: 13 }}
    //             >
    //               <i className={`bi ${s.icon} me-1`}></i>
    //               {s.label}
    //             </span>
    //             <span className="fw-bold text-success" style={{ fontSize: 22 }}>
    //               {fmtEur(item.amount)}
    //             </span>
    //           </div>

    //           {/* Stripe IDs */}
    //           <div className="d-flex flex-column gap-2">
    //             <div className="d-flex align-items-center justify-content-between">
    //               <span className="text-muted" style={{ fontSize: 11 }}>
    //                 <i className="bi bi-credit-card me-1"></i>PaymentIntent
    //               </span>
    //               <StripeLink id={item.Reservation.PaymentIntentId} type="pi" />
    //             </div>
    //             <div className="d-flex align-items-center justify-content-between">
    //               <span className="text-muted" style={{ fontSize: 11 }}>
    //                 <i className="bi bi-arrow-right-circle me-1"></i>Virement
    //                 hôte
    //               </span>
    //               <StripeLink
    //                 id={item.payoutId || item.transactionID}
    //                 type="tr"
    //               />
    //             </div>
    //             {item.refundId && (
    //               <div className="d-flex align-items-center justify-content-between">
    //                 <span className="text-muted" style={{ fontSize: 11 }}>
    //                   <i className="bi bi-arrow-counterclockwise me-1"></i>
    //                   Remboursement
    //                 </span>
    //                 <StripeLink id={item.refundId} type="re" />
    //               </div>
    //             )}
    //           </div>

    //           {/* Détails Stripe live */}
    //           {piDetails && (
    //             <div
    //               className="mt-2 p-2 bg-body-tertiary rounded-2"
    //               style={{ fontSize: 11 }}
    //             >
    //               <span className="text-muted me-2">Stripe status :</span>
    //               <span
    //                 className={`badge rounded-pill ${piDetails.status === "succeeded" ? "bg-success-subtle text-success" : "bg-warning-subtle text-warning"}`}
    //               >
    //                 {piDetails.status}
    //               </span>
    //               {piDetails.charges?.data?.[0]?.payment_method_details
    //                 ?.type && (
    //                 <span className="ms-2 text-muted">
    //                   · {piDetails.charges.data[0].payment_method_details.type}
    //                 </span>
    //               )}
    //             </div>
    //           )}

    //           <div className="row g-2 mt-1" style={{ fontSize: 12 }}>
    //             <div className="col-6">
    //               <p className="mb-0 text-muted">Date paiement</p>
    //               <p className="mb-0 fw-semibold">
    //                 {moment(item.createdAt).format("DD MMM YYYY · HH:mm")}
    //               </p>
    //             </div>
    //             <div className="col-6">
    //               <p className="mb-0 text-muted">Méthode</p>
    //               <p className="mb-0 fw-semibold">
    //                 {item.paiement_method || "—"}
    //               </p>
    //             </div>
    //           </div>
    //         </div>

    //         {/* ── Parties ── */}
    //         <div className="px-3 py-3 border-bottom">
    //           <p className="fw-semibold mb-2" style={{ fontSize: 13 }}>
    //             Parties
    //           </p>
    //           <div className="row g-2">
    //             <div className="col-6">
    //               <div className="p-2 rounded-3 border h-100">
    //                 <p className="mb-1" style={{ fontSize: 10 }}>
    //                   <i className="bi bi-person-fill text-success me-1"></i>
    //                   <span className="text-muted">Locataire (payeur)</span>
    //                 </p>
    //                 <p className="mb-0 fw-semibold" style={{ fontSize: 13 }}>
    //                   {guest?.firstName} {guest?.lastName}
    //                 </p>
    //                 <p
    //                   className="mb-0 text-muted text-truncate"
    //                   style={{ fontSize: 11 }}
    //                 >
    //                   {guest?.email}
    //                 </p>
    //               </div>
    //             </div>
    //             <div className="col-6">
    //               <div className="p-2 rounded-3 border h-100">
    //                 <p className="mb-1" style={{ fontSize: 10 }}>
    //                   <i className="bi bi-house-fill text-primary me-1"></i>
    //                   <span className="text-muted">Hôte (bénéficiaire)</span>
    //                 </p>
    //                 <p className="mb-0 fw-semibold" style={{ fontSize: 13 }}>
    //                   {host?.firstName} {host?.lastName}
    //                 </p>
    //                 <p
    //                   className="mb-0 text-muted text-truncate"
    //                   style={{ fontSize: 11 }}
    //                 >
    //                   {host?.email}
    //                 </p>
    //               </div>
    //             </div>
    //           </div>
    //         </div>

    //         {/* ── Actions ── */}
    //         <div className="px-3 py-3 border-bottom">
    //           <p className="fw-semibold mb-2" style={{ fontSize: 13 }}>
    //             Actions
    //           </p>
    //           <div className="d-flex flex-column gap-2">
    //             {/* Payer hôte */}
    //             {canPay && (
    //               <button
    //                 className="btn btn-success d-flex align-items-center gap-2"
    //                 onClick={handlePay}
    //                 disabled={loading}
    //               >
    //                 <i className="bi bi-send-fill"></i>
    //                 Virer à l'hôte · {fmtEur(item.amount)}
    //               </button>
    //             )}

    //             {/* Réessayer virement échoué */}
    //             {canRetry && (
    //               <div>
    //                 <div className="alert alert-danger py-2 px-3 mb-2 d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
    //                   <i className="bi bi-exclamation-circle-fill flex-shrink-0"></i>
    //                   Le dernier virement a échoué. Vérifiez le compte Stripe de l'hôte avant de réessayer.
    //                 </div>
    //                 <button
    //                   className="btn btn-warning d-flex align-items-center gap-2 w-100"
    //                   onClick={handlePay}
    //                   disabled={loading}
    //                 >
    //                   {loading
    //                     ? <span className="spinner-border spinner-border-sm" />
    //                     : <i className="bi bi-arrow-clockwise"></i>
    //                   }
    //                   Réessayer le virement · {fmtEur(item.amount)}
    //                 </button>
    //               </div>
    //             )}

    //             {/* Ouvrir litige */}
    //             {canDispute && (
    //               <button
    //                 className="btn btn-outline-warning d-flex align-items-center gap-2"
    //                 onClick={handleDispute}
    //                 disabled={loading}
    //               >
    //                 <i className="bi bi-exclamation-triangle-fill"></i>
    //                 Ouvrir un litige
    //               </button>
    //             )}

    //             {/* Résoudre litige */}
    //             {canResolve && (
    //               <div className="d-flex gap-2">
    //                 <button
    //                   className="btn btn-outline-success flex-fill d-flex align-items-center justify-content-center gap-2"
    //                   onClick={handleResolve}
    //                   disabled={loading}
    //                 >
    //                   <i className="bi bi-check-circle-fill"></i>
    //                   Résoudre → Payer hôte
    //                 </button>
    //                 <button
    //                   className="btn btn-outline-danger flex-fill d-flex align-items-center justify-content-center gap-2"
    //                   onClick={handleRefund}
    //                   disabled={loading}
    //                 >
    //                   <i className="bi bi-arrow-counterclockwise"></i>
    //                   Résoudre → Rembourser
    //                 </button>
    //               </div>
    //             )}

    //             {/* Remboursement */}
    //             {canRefund && !canResolve && (
    //               <div className="card border-0 bg-danger bg-opacity-10 rounded-3">
    //                 <div className="card-body p-3">
    //                   <p
    //                     className="mb-2 fw-semibold text-danger"
    //                     style={{ fontSize: 13 }}
    //                   >
    //                     <i className="bi bi-arrow-counterclockwise me-1"></i>
    //                     Remboursement Stripe
    //                   </p>
    //                   <p className="text-muted mb-2" style={{ fontSize: 11 }}>
    //                     Utilise le <code>paymentIntentId</code> pour rembourser
    //                     directement le locataire via Stripe.
    //                   </p>

    //                   <div className="row g-2 mb-2">
    //                     <div className="col-7">
    //                       <label
    //                         className="form-label mb-1"
    //                         style={{ fontSize: 11 }}
    //                       >
    //                         Montant (€)
    //                       </label>
    //                       <div className="input-group input-group-sm">
    //                         <span className="input-group-text">€</span>
    //                         <input
    //                           type="number"
    //                           className="form-control"
    //                           value={refAmount}
    //                           onChange={(e) => setRefAmount(e.target.value)}
    //                           placeholder={item.amount}
    //                           step="0.01"
    //                           min="0.50"
    //                           max={item.amount}
    //                         />
    //                       </div>
    //                       <small
    //                         className="text-muted"
    //                         style={{ fontSize: 10 }}
    //                       >
    //                         Max : {fmtEur(item.amount)}
    //                       </small>
    //                     </div>
    //                     <div className="col-5">
    //                       <label
    //                         className="form-label mb-1"
    //                         style={{ fontSize: 11 }}
    //                       >
    //                         Motif
    //                       </label>
    //                       <select
    //                         className="form-select form-select-sm"
    //                         value={refReason}
    //                         onChange={(e) => setRefReason(e.target.value)}
    //                       >
    //                         <option value="requested_by_customer">
    //                           Client
    //                         </option>
    //                         <option value="duplicate">Doublon</option>
    //                         <option value="fraudulent">Fraude</option>
    //                       </select>
    //                     </div>
    //                   </div>

    //                   {!item.Reservation.PaymentIntentId && (
    //                     <div
    //                       className="alert alert-warning py-1 px-2 mb-2"
    //                       style={{ fontSize: 11 }}
    //                     >
    //                       <i className="bi bi-exclamation-triangle me-1"></i>
    //                       <code>paymentIntentId</code> manquant — remboursement
    //                       impossible.
    //                     </div>
    //                   )}

    //                   <button
    //                     className="btn btn-sm btn-danger w-100"
    //                     onClick={handleRefund}
    //                     disabled={
    //                       loading ||
    //                       !item.Reservation.PaymentIntentId ||
    //                       !refAmount
    //                     }
    //                   >
    //                     <i className="bi bi-arrow-counterclockwise me-1"></i>
    //                     Rembourser le locataire
    //                   </button>
    //                 </div>
    //               </div>
    //             )}
    //           </div>
    //         </div>

    //         {/* ── Note interne ── */}
    //         <div className="px-3 py-3">
    //           <p className="fw-semibold mb-2" style={{ fontSize: 13 }}>
    //             <i className="bi bi-journal-text me-1 text-muted"></i>Note
    //             interne admin
    //           </p>
    //           <textarea
    //             className="form-control form-control-sm mb-2"
    //             rows={3}
    //             placeholder="Visible uniquement par les admins..."
    //             value={note}
    //             onChange={(e) => setNote(e.target.value)}
    //             style={{ fontSize: 12, resize: "none" }}
    //           />
    //           <button
    //             className="btn btn-sm btn-outline-secondary w-100"
    //             onClick={saveNote}
    //           >
    //             <i className="bi bi-floppy me-1"></i>Sauvegarder
    //           </button>
    //         </div>
    //       </>
    //     )}
    //   </div>
    // </div>
    <Offcanvas
      show={!!item} // S'affiche si un item est sélectionné
      onHide={onClose}
      placement="end"
      style={{ width: 'min(520px, 96vw)' }}
    >
      {/* Header avec style personnalisé */}
      <div className="offcanvas-header border-bottom bg-body-tertiary">
        <div>
          <h6 className="mb-0 fw-bold">
            <i className="bi bi-shield-exclamation me-2 text-primary"></i>
            {item ? `Réservation #${item.reservationId}` : 'Paiement'}
          </h6>
          {item && <small className="text-muted">Paiement interne #{item.id}</small>}
        </div>
        <button
          type="button"
          className="btn-close"
          onClick={onClose} // Utilise votre fonction onClose
        />
      </div>

      <div className="offcanvas-body p-0 overflow-y-auto">
        {!item ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-inbox" style={{ fontSize: 36 }}></i>
            <p className="mt-3 mb-0">Sélectionnez un paiement</p>
          </div>
        ) : (
          <>
            {/* ── Statut + montant ── */}
            <div className="px-3 py-3 border-bottom">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className={`badge rounded-pill px-3 py-2 ${s.bg}`} style={{ fontSize: 13 }}>
                  <i className={`bi ${s.icon} me-1`}></i>
                  {s.label}
                </span>
                <span className="fw-bold text-success" style={{ fontSize: 22 }}>
                  {fmtEur(item.amount)}
                </span>
              </div>

              {/* Stripe IDs */}
              <div className="d-flex flex-column gap-2">
                <div className="d-flex align-items-center justify-content-between">
                  <span className="text-muted" style={{ fontSize: 11 }}>
                    <i className="bi bi-credit-card me-1"></i>PaymentIntent
                  </span>
                  <StripeLink id={item.Reservation.PaymentIntentId} type="pi" />
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="text-muted" style={{ fontSize: 11 }}>
                    <i className="bi bi-arrow-right-circle me-1"></i>Virement hôte
                  </span>
                  <StripeLink id={item.payoutId || item.transactionID} type="tr" />
                </div>
                {item.refundId && (
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="text-muted" style={{ fontSize: 11 }}>
                      <i className="bi bi-arrow-counterclockwise me-1"></i>
                      Remboursement
                    </span>
                    <StripeLink id={item.refundId} type="re" />
                  </div>
                )}
              </div>

              {/* Détails Stripe live */}
              {piDetails && (
                <div className="mt-2 p-2 bg-body-tertiary rounded-2" style={{ fontSize: 11 }}>
                  <span className="text-muted me-2">Stripe status :</span>
                  <span
                    className={`badge rounded-pill ${piDetails.status === 'succeeded' ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'}`}
                  >
                    {piDetails.status}
                  </span>
                  {piDetails.charges?.data?.[0]?.payment_method_details?.type && (
                    <span className="ms-2 text-muted">
                      · {piDetails.charges.data[0].payment_method_details.type}
                    </span>
                  )}
                </div>
              )}

              <div className="row g-2 mt-1" style={{ fontSize: 12 }}>
                <div className="col-6">
                  <p className="mb-0 text-muted">Date paiement</p>
                  <p className="mb-0 fw-semibold">
                    {moment(item.createdAt).format('DD MMM YYYY · HH:mm')}
                  </p>
                </div>
                <div className="col-6">
                  <p className="mb-0 text-muted">Méthode</p>
                  <p className="mb-0 fw-semibold">{item.paiement_method || '—'}</p>
                </div>
              </div>
            </div>

            {/* ── Parties ── */}
            <div className="px-3 py-3 border-bottom">
              <p className="fw-semibold mb-2" style={{ fontSize: 13 }}>
                Parties
              </p>
              <div className="row g-2">
                <div className="col-6">
                  <div className="p-2 rounded-3 border h-100">
                    <p className="mb-1" style={{ fontSize: 10 }}>
                      <i className="bi bi-person-fill text-success me-1"></i>
                      <span className="text-muted">Locataire (payeur)</span>
                    </p>
                    <p className="mb-0 fw-semibold" style={{ fontSize: 13 }}>
                      {guest?.firstName} {guest?.lastName}
                    </p>
                    <p className="mb-0 text-muted text-truncate" style={{ fontSize: 11 }}>
                      {guest?.email}
                    </p>
                  </div>
                </div>
                <div className="col-6">
                  <div className="p-2 rounded-3 border h-100">
                    <p className="mb-1" style={{ fontSize: 10 }}>
                      <i className="bi bi-house-fill text-primary me-1"></i>
                      <span className="text-muted">Hôte (bénéficiaire)</span>
                    </p>
                    <p className="mb-0 fw-semibold" style={{ fontSize: 13 }}>
                      {host?.firstName} {host?.lastName}
                    </p>
                    <p className="mb-0 text-muted text-truncate" style={{ fontSize: 11 }}>
                      {host?.email}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Actions ── */}
            <div className="px-3 py-3 border-bottom">
              <p className="fw-semibold mb-2" style={{ fontSize: 13 }}>
                Actions
              </p>
              <div className="d-flex flex-column gap-2">
                {canPay && (
                  <button
                    className="btn btn-success d-flex align-items-center gap-2"
                    onClick={handlePay}
                    disabled={loading}
                  >
                    <i className="bi bi-send-fill"></i>
                    Virer à l'hôte · {fmtEur(item.amount)}
                  </button>
                )}

                {canRetry && (
                  <div>
                    <div
                      className="alert alert-danger py-2 px-3 mb-2 d-flex align-items-center gap-2"
                      style={{ fontSize: 12 }}
                    >
                      <i className="bi bi-exclamation-circle-fill flex-shrink-0"></i>
                      Le dernier virement a échoué.
                    </div>
                    <button
                      className="btn btn-warning d-flex align-items-center gap-2 w-100"
                      onClick={handlePay}
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : (
                        <i className="bi bi-arrow-clockwise"></i>
                      )}
                      Réessayer le virement
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Offcanvas>
  );
}

/* ══════════════ Carte paiement ══════════════ */
function PaiementRow({ item, onSelect }) {
  const s = PAIEMENT_STATUS[item.paiementStatus] ?? PAIEMENT_STATUS['0'];
  const host = item.Reservation?.Host;
  const guest = item.Reservation?.Invite;

  return (
    <div
      className={`card border-0 shadow-sm rounded-3 mb-2 overflow-hidden border-start border-3 ${s.border}`}
    >
      <div className="card-body p-3">
        <div className="d-flex align-items-start gap-3">
          <div className="flex-grow-1 overflow-hidden">
            {/* Ligne 1 : statut + resa + date */}
            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
              <span className={`badge rounded-pill ${s.bg}`} style={{ fontSize: 10 }}>
                <i className={`bi ${s.icon} me-1`}></i>
                {s.label}
              </span>
              <span className="text-muted font-monospace" style={{ fontSize: 11 }}>
                #{item.reservationId}
              </span>
              <span className="text-muted ms-auto flex-shrink-0" style={{ fontSize: 11 }}>
                {moment(item.createdAt).fromNow()}
              </span>
            </div>

            {/* Ligne 2 : hôte → invité */}
            <div className="d-flex align-items-center gap-1 mb-1" style={{ fontSize: 12 }}>
              <i className="bi bi-house text-primary" style={{ fontSize: 11 }}></i>
              <span className="fw-semibold text-truncate">
                {host?.firstName} {host?.lastName}
              </span>
              <i className="bi bi-arrow-right text-muted mx-1" style={{ fontSize: 10 }}></i>
              <i className="bi bi-person text-success" style={{ fontSize: 11 }}></i>
              <span className="text-muted text-truncate">
                {guest?.firstName} {guest?.lastName}
              </span>
            </div>

            {/* Ligne 3 : Stripe IDs */}
            <div className="d-flex gap-3 flex-wrap">
              {item.Reservation.PaymentIntentId && (
                <span
                  style={{ fontSize: 10 }}
                  className="text-muted d-flex align-items-center gap-1"
                >
                  <i className="bi bi-credit-card"></i>
                  <StripeLink id={item.Reservation.PaymentIntentId} type="pi" />
                </span>
              )}
              {(item.payoutId || item.transactionID) && (
                <span
                  style={{ fontSize: 10 }}
                  className="text-muted d-flex align-items-center gap-1"
                >
                  <i className="bi bi-arrow-right-circle"></i>
                  <StripeLink id={item.payoutId || item.transactionID} type="tr" />
                </span>
              )}
            </div>

            {item.notes && (
              <p className="mb-0 text-muted fst-italic mt-1" style={{ fontSize: 11 }}>
                <i className="bi bi-journal-text me-1"></i>
                {item.notes}
              </p>
            )}
          </div>

          {/* Montant + bouton */}
          <div className="d-flex flex-column align-items-end gap-2 flex-shrink-0">
            <span className="fw-bold text-success" style={{ fontSize: 15 }}>
              {fmtEur(item.amount)}
            </span>
            <button
              className="btn btn-sm btn-outline-primary"
              data-bs-toggle="offcanvas"
              data-bs-target="#disputePanel"
              onClick={() => onSelect(item)}
              style={{ fontSize: 11 }}
            >
              <i className="bi bi-sliders me-1"></i>Gérer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════ Page ══════════════ */
function AdminPaiements() {
  const axios = useAxios();
  const [paiements, setPaiements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchReport, setBatchReport] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);

  // ── DEV: activation comptes Stripe test ──
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [stripeUsers, setStripeUsers] = useState([]); // { id, firstName, lastName, email, stripeAccountId, status }
  const [stripeProgress, setStripeProgress] = useState(false);

  const load = () => {
    setIsLoading(true);
    axios
      .get('/paiements')
      .then(({ data }) => {
        if (Array.isArray(data))
          setPaiements(data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  /* ── Charger tous les users avec stripeAccountId ── */
  async function openStripeModal() {
    setShowStripeModal(true);
    setStripeProgress(false);
    try {
      const { data } = await axios.get('/users');
      const withStripe = (Array.isArray(data) ? data : [])
        .filter((u) => u.stripeAccountId)
        .map((u) => ({ ...u, activationStatus: 'idle' })); // idle | loading | success | error
      setStripeUsers(withStripe);
    } catch {
      toast.error('Impossible de récupérer les utilisateurs');
      setShowStripeModal(false);
    }
  }

  /* ── Activer un seul compte ── */
  async function activateSingle(userId) {
    setStripeUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, activationStatus: 'loading' } : u))
    );
    const user = stripeUsers.find((u) => u.id === userId);
    try {
      await axios.post(`/stripe/activate-test-account/${user.stripeAccountId}`);
      setStripeUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, activationStatus: 'success' } : u))
      );
    } catch (err) {
      setStripeUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? {
                ...u,
                activationStatus: 'error',
                errorMsg: err?.response?.data?.error || err.message,
              }
            : u
        )
      );
    }
  }

  /* ── Activer tous les comptes en séquence ── */
  async function activateAll() {
    setStripeProgress(true);
    for (const user of stripeUsers) {
      if (user.activationStatus === 'success') continue;
      await activateSingle(user.id);
    }
    setStripeProgress(false);
    toast.success('Activation terminée');
  }

  async function confirmBatchPayout() {
    setShowBatchModal(false);
    setBatchLoading(true);
    setBatchReport(null);
    try {
      const { data } = await axios.post('/admin/payout-batch');
      setBatchReport(data.report);
      toast.success(`✓ Batch terminé — ${data.report.success} virement(s) effectué(s)`);
      load();
    } catch {
      toast.error('Erreur lors du batch de virements');
    } finally {
      setBatchLoading(false);
    }
  }

  const filtered = paiements.filter((p) => {
    const matchTab = activeTab === 'all' || p.paiementStatus === activeTab;
    const matchSearch =
      !search ||
      String(p.reservationId).includes(search) ||
      p.Reservation.PaymentIntentId?.toLowerCase().includes(search.toLowerCase()) ||
      p.payoutId?.toLowerCase().includes(search.toLowerCase()) ||
      p.transactionID?.toLowerCase().includes(search.toLowerCase()) ||
      p.Reservation?.Host?.email?.toLowerCase().includes(search.toLowerCase()) ||
      p.Reservation?.Invite?.email?.toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  const countByTab = (k) =>
    k === 'all' ? paiements.length : paiements.filter((p) => p.paiementStatus === k).length;
  const sumByStatus = (k) =>
    paiements
      .filter((p) => p.paiementStatus === k)
      .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  return (
    <>
      <ToastContainer />
      <DisputePanel
        item={selected}
        onUpdate={() => {
          load();
          setSelected(null);
        }}
        onClose={() => setSelected(null)}
      />

      <section className="bg-body-tertiary min-vh-100">
        <div className="container py-3 pb-5">
          <div className="row g-3">
            {/* Sidebar desktop */}
            <div className="col-md-3 d-none d-md-block">
              <SideBarAdmin />
            </div>

            <div className="col-md-9">
              {/* Header */}
              <div className="d-flex align-items-center gap-2 mb-3">
                <button
                  className="btn btn-sm btn-light d-md-none"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#adminSidebarMobile"
                >
                  <i className="bi bi-list"></i>
                </button>
                <h5 className="mb-0 fw-bold flex-grow-1">
                  <i className="bi bi-credit-card-fill me-2 text-primary"></i>
                  Paiements & Litiges
                </h5>
                <button
                  className="btn btn-sm btn-outline-warning d-flex align-items-center gap-2"
                  onClick={openStripeModal}
                  title="[DEV] Activer les comptes Stripe Connect test"
                >
                  <i className="bi bi-lightning-charge-fill"></i>
                  <span className="d-none d-sm-inline">Stripe test</span>
                </button>
                <button
                  className="btn btn-sm btn-success d-flex align-items-center gap-2"
                  onClick={() => setShowBatchModal(true)}
                  disabled={batchLoading}
                  title="Virer en masse tous les hôtes en attente"
                >
                  {batchLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" />
                      Traitement…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send-fill"></i>
                      <span className="d-none d-sm-inline">Virements en masse</span>
                    </>
                  )}
                </button>
              </div>

              {/* ── Tableau de bord financier ── */}
              {!isLoading &&
                paiements.length > 0 &&
                (() => {
                  const now = moment();
                  const totalAll = paiements.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
                  const totalMois = paiements
                    .filter((p) => moment(p.createdAt).isSame(now, 'month'))
                    .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
                  const commission = sumByStatus('1') * 0.2;
                  const tauxLitige =
                    totalAll > 0 ? ((sumByStatus('3') / totalAll) * 100).toFixed(1) : '0.0';
                  const tauxRembours =
                    totalAll > 0 ? ((sumByStatus('4') / totalAll) * 100).toFixed(1) : '0.0';
                  return (
                    <div className="card border-0 shadow-sm rounded-4 mb-3 overflow-hidden">
                      <div
                        className="card-header px-3 py-2 border-0"
                        style={{
                          background: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
                        }}
                      >
                        <p
                          className="mb-0 fw-semibold text-white d-flex align-items-center gap-2"
                          style={{ fontSize: 13 }}
                        >
                          <i className="bi bi-bar-chart-fill"></i>
                          Vue financière globale
                          <span
                            className="ms-auto badge bg-white bg-opacity-25 text-white rounded-pill"
                            style={{ fontSize: 10 }}
                          >
                            {now.format('MMMM YYYY')}
                          </span>
                        </p>
                      </div>
                      <div className="card-body p-0">
                        <div className="row g-0">
                          {[
                            {
                              label: 'Volume total collecté',
                              value: fmtEur(totalAll),
                              icon: 'bi-cash-stack',
                              color: '#1d4ed8',
                              border: true,
                            },
                            {
                              label: 'Volume ce mois',
                              value: fmtEur(totalMois),
                              icon: 'bi-calendar3',
                              color: '#7c3aed',
                              border: false,
                            },
                            {
                              label: 'Commission perçue',
                              value: fmtEur(commission),
                              icon: 'bi-percent',
                              color: '#0891b2',
                              border: true,
                            },
                            {
                              label: 'Taux litige',
                              value: `${tauxLitige} %`,
                              icon: 'bi-exclamation-triangle',
                              color: countByTab('3') > 0 ? '#ef4444' : '#6b7280',
                              border: false,
                            },
                            {
                              label: 'Taux remboursement',
                              value: `${tauxRembours} %`,
                              icon: 'bi-arrow-counterclockwise',
                              color: '#0891b2',
                              border: true,
                            },
                            {
                              label: 'Hôtes en attente',
                              value: `${countByTab('0')} hôte${countByTab('0') !== 1 ? 's' : ''}`,
                              icon: 'bi-hourglass-split',
                              color: '#f59e0b',
                              border: false,
                            },
                          ].map(({ label, value, icon, color, border }) => (
                            <div
                              key={label}
                              className={`col-6 col-md-4 px-3 py-3 ${border ? 'border-end' : ''} border-bottom`}
                            >
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <i className={`bi ${icon}`} style={{ color, fontSize: 13 }}></i>
                                <span className="text-muted" style={{ fontSize: 11 }}>
                                  {label}
                                </span>
                              </div>
                              <p className="mb-0 fw-bold" style={{ fontSize: 16, color }}>
                                {value}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}

              {/* KPI */}
              <div className="d-flex flex-column gap-2 mb-3">
                {[
                  {
                    label: 'En attente de virement',
                    value: fmtEur(sumByStatus('0')),
                    color: '#f59e0b',
                    icon: 'bi-hourglass-split',
                  },
                  {
                    label: 'Virés aux hôtes',
                    value: fmtEur(sumByStatus('1')),
                    color: '#22c55e',
                    icon: 'bi-check-circle-fill',
                  },
                  {
                    label: 'Litiges ouverts',
                    value: countByTab('3') + ' réservation(s)',
                    color: '#ef4444',
                    icon: 'bi-exclamation-triangle-fill',
                  },
                  {
                    label: 'Remboursements effectués',
                    value: fmtEur(sumByStatus('4')),
                    color: '#06b6d4',
                    icon: 'bi-arrow-counterclockwise',
                  },
                ].map(({ label, value, color, icon }) => (
                  <div key={label} className="card border-0 shadow-sm rounded-3">
                    <div className="card-body px-3 py-2 d-flex align-items-center gap-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                        style={{
                          width: 36,
                          height: 36,
                          background: `${color}18`,
                        }}
                      >
                        <i className={`bi ${icon}`} style={{ color, fontSize: 16 }}></i>
                      </div>
                      <p className="mb-0 text-muted flex-grow-1" style={{ fontSize: 12 }}>
                        {label}
                      </p>
                      <p className="mb-0 fw-bold flex-shrink-0" style={{ fontSize: 15, color }}>
                        {value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Rapport batch */}
              {batchReport && (
                <div className="card border-0 shadow-sm rounded-3 mb-3">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p className="fw-semibold mb-0" style={{ fontSize: 13 }}>
                        <i className="bi bi-clipboard-check me-2 text-success"></i>
                        Rapport du dernier batch
                      </p>
                      <button
                        className="btn-close btn-sm"
                        style={{ fontSize: 10 }}
                        onClick={() => setBatchReport(null)}
                      />
                    </div>
                    <div className="d-flex gap-3 flex-wrap" style={{ fontSize: 12 }}>
                      <span className="text-success fw-semibold">
                        <i className="bi bi-check-circle-fill me-1"></i>
                        {batchReport.success} viré(s)
                      </span>
                      <span className="text-warning fw-semibold">
                        <i className="bi bi-exclamation-circle-fill me-1"></i>
                        {batchReport.skipped} ignoré(s)
                      </span>
                      <span className="text-danger fw-semibold">
                        <i className="bi bi-x-circle-fill me-1"></i>
                        {batchReport.failed} échoué(s)
                      </span>
                    </div>
                    {batchReport.details?.filter((d) => d.status !== 'success').length > 0 && (
                      <div className="mt-2 border-top pt-2">
                        {batchReport.details
                          .filter((d) => d.status !== 'success')
                          .map((d, i) => (
                            <p key={i} className="mb-1 text-muted" style={{ fontSize: 11 }}>
                              <span
                                className={`badge rounded-pill me-1 ${d.status === 'skipped' ? 'bg-warning-subtle text-warning' : 'bg-danger-subtle text-danger'}`}
                              >
                                #{d.resaId}
                              </span>
                              {d.reason}
                            </p>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recherche */}
              <div className="input-group input-group-sm mb-3">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Résa, PaymentIntent pi_xxx, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button className="btn btn-outline-secondary" onClick={() => setSearch('')}>
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>

              {/* Tabs */}
              <div
                className="d-flex overflow-x-auto border-bottom mb-3"
                style={{ scrollbarWidth: 'none' }}
              >
                {TABS.map(({ key, label }) => {
                  const count = countByTab(key);
                  return (
                    <button
                      key={key}
                      className={`btn btn-sm flex-shrink-0 rounded-0 border-0 border-bottom border-3 px-3 py-2 ${activeTab === key ? 'border-primary text-primary fw-semibold' : 'border-transparent text-muted'}`}
                      style={{ fontSize: 12 }}
                      onClick={() => setActiveTab(key)}
                    >
                      {label}
                      {count > 0 && (
                        <span
                          className={`badge rounded-pill ms-1 ${key === '3' ? 'bg-danger' : key === '0' ? 'bg-warning text-dark' : 'bg-secondary'}`}
                          style={{ fontSize: 9 }}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Liste */}
              {isLoading && (
                <div className="text-center py-5 text-muted">
                  <div className="spinner-border spinner-border-sm me-2" />
                  Chargement...
                </div>
              )}
              {!isLoading && filtered.length === 0 && (
                <div className="card border-0 shadow-sm rounded-3">
                  <div className="card-body text-center py-5 text-muted">
                    <i className="bi bi-inbox" style={{ fontSize: 36 }}></i>
                    <p className="mt-3 mb-0">Aucun paiement trouvé</p>
                  </div>
                </div>
              )}
              {filtered.map((item) => (
                <PaiementRow key={item.id} item={item} onSelect={setSelected} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════ Modal DEV — Activation comptes Stripe test ══════ */}
      {showStripeModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{
            background: 'rgba(0,0,0,0.55)',
            position: 'fixed',
            inset: 0,
            zIndex: 1070,
            overflowY: 'auto',
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg my-4"
            style={{ maxWidth: 600 }}
          >
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              {/* Header */}
              <div
                className="px-4 pt-4 pb-3"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25"
                      style={{ width: 44, height: 44, flexShrink: 0 }}
                    >
                      <i
                        className="bi bi-lightning-charge-fill text-white"
                        style={{ fontSize: 18 }}
                      ></i>
                    </div>
                    <div>
                      <h5 className="mb-0 fw-bold text-white">Activation comptes Stripe test</h5>
                      <span
                        className="badge bg-dark bg-opacity-50 text-white rounded-pill"
                        style={{ fontSize: 10 }}
                      >
                        DEV ONLY
                      </span>
                    </div>
                  </div>
                  <button
                    className="btn-close btn-close-white"
                    onClick={() => setShowStripeModal(false)}
                  />
                </div>
              </div>

              {/* Body */}
              <div className="px-4 py-3">
                {/* Stats + bouton tout activer */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex gap-3" style={{ fontSize: 12 }}>
                    <span className="text-muted">
                      <i className="bi bi-people me-1"></i>
                      {stripeUsers.length} compte(s) Stripe trouvé(s)
                    </span>
                    <span className="text-success">
                      <i className="bi bi-check-circle-fill me-1"></i>
                      {stripeUsers.filter((u) => u.activationStatus === 'success').length} activé(s)
                    </span>
                    {stripeUsers.filter((u) => u.activationStatus === 'error').length > 0 && (
                      <span className="text-danger">
                        <i className="bi bi-x-circle-fill me-1"></i>
                        {stripeUsers.filter((u) => u.activationStatus === 'error').length} échoué(s)
                      </span>
                    )}
                  </div>
                  <button
                    className="btn btn-sm btn-warning d-flex align-items-center gap-2 rounded-pill"
                    onClick={activateAll}
                    disabled={stripeProgress || stripeUsers.length === 0}
                  >
                    {stripeProgress ? (
                      <>
                        <span className="spinner-border spinner-border-sm" />
                        En cours…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-lightning-charge-fill"></i>Tout activer
                      </>
                    )}
                  </button>
                </div>

                {/* Liste des comptes */}
                {stripeUsers.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-people" style={{ fontSize: 32 }}></i>
                    <p className="mt-2 mb-0 small">
                      Aucun utilisateur avec un compte Stripe Connect
                    </p>
                  </div>
                ) : (
                  <div
                    className="d-flex flex-column gap-2"
                    style={{ maxHeight: 400, overflowY: 'auto' }}
                  >
                    {stripeUsers.map((user) => {
                      const st = user.activationStatus;
                      return (
                        <div
                          key={user.id}
                          className={`rounded-3 p-3 border d-flex align-items-center gap-3 ${
                            st === 'success'
                              ? 'border-success bg-success bg-opacity-10'
                              : st === 'error'
                                ? 'border-danger  bg-danger  bg-opacity-10'
                                : 'border-0 bg-body-tertiary'
                          }`}
                        >
                          {/* Avatar initiales */}
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle bg-secondary text-white flex-shrink-0"
                            style={{
                              width: 36,
                              height: 36,
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            {(user.firstName?.[0] || '?').toUpperCase()}
                          </div>

                          {/* Infos */}
                          <div className="flex-grow-1 overflow-hidden">
                            <p className="mb-0 fw-semibold" style={{ fontSize: 13 }}>
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="mb-0 text-muted text-truncate" style={{ fontSize: 11 }}>
                              {user.email}
                            </p>
                            <p
                              className="mb-0 font-monospace text-truncate"
                              style={{ fontSize: 10, color: '#6b7280' }}
                            >
                              {user.stripeAccountId}
                            </p>
                            {st === 'error' && user.errorMsg && (
                              <p className="mb-0 text-danger" style={{ fontSize: 10 }}>
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                {user.errorMsg}
                              </p>
                            )}
                          </div>

                          {/* Statut / bouton */}
                          <div className="flex-shrink-0">
                            {st === 'idle' && (
                              <button
                                className="btn btn-sm btn-outline-warning rounded-pill px-3"
                                style={{ fontSize: 11 }}
                                onClick={() => activateSingle(user.id)}
                                disabled={stripeProgress}
                              >
                                Activer
                              </button>
                            )}
                            {st === 'loading' && (
                              <span className="spinner-border spinner-border-sm text-warning" />
                            )}
                            {st === 'success' && (
                              <span
                                className="badge bg-success rounded-pill px-3"
                                style={{ fontSize: 11 }}
                              >
                                <i className="bi bi-check-circle-fill me-1"></i>
                                Activé
                              </span>
                            )}
                            {st === 'error' && (
                              <button
                                className="btn btn-sm btn-outline-danger rounded-pill px-3"
                                style={{ fontSize: 11 }}
                                onClick={() => activateSingle(user.id)}
                                disabled={stripeProgress}
                              >
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Retry
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 pb-4 d-flex justify-content-end">
                <button
                  className="btn btn-outline-secondary rounded-pill px-4"
                  onClick={() => setShowStripeModal(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════ Modal confirmation batch payout ══════ */}
      {showBatchModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{
            background: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 440 }}>
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              {/* Header gradient vert */}
              <div
                className="px-4 pt-4 pb-3"
                style={{
                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25"
                    style={{ width: 48, height: 48, flexShrink: 0 }}
                  >
                    <i className="bi bi-send-fill text-white" style={{ fontSize: 20 }}></i>
                  </div>
                  <div>
                    <h5 className="mb-0 fw-bold text-white">Virements en masse</h5>
                    <p className="mb-0 text-white text-opacity-75" style={{ fontSize: 12 }}>
                      Action irréversible — à confirmer
                    </p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="px-4 py-3">
                <p className="mb-3 text-body" style={{ fontSize: 14 }}>
                  Cette action va déclencher le virement Stripe vers <strong>tous les hôtes</strong>{' '}
                  dont le paiement est en statut{' '}
                  <span className="badge bg-warning-subtle text-warning rounded-pill">
                    <i className="bi bi-clock-history me-1"></i>En attente
                  </span>
                  .
                </p>

                {/* Récapitulatif */}
                <div
                  className="rounded-3 p-3 mb-3"
                  style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}
                >
                  <div className="d-flex gap-3 flex-wrap">
                    <div className="text-center flex-fill">
                      <p className="mb-0 fw-bold text-success" style={{ fontSize: 20 }}>
                        {paiements.filter((p) => p.paiementStatus === '0').length}
                      </p>
                      <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
                        En attente
                      </p>
                    </div>
                    <div className="text-center flex-fill border-start border-end px-3">
                      <p className="mb-0 fw-bold text-success" style={{ fontSize: 20 }}>
                        {fmtEur(
                          paiements
                            .filter((p) => p.paiementStatus === '0')
                            .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0) * 0.8
                        )}
                      </p>
                      <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
                        Total hôtes (80%)
                      </p>
                    </div>
                    <div className="text-center flex-fill">
                      <p className="mb-0 fw-bold text-primary" style={{ fontSize: 20 }}>
                        {fmtEur(
                          paiements
                            .filter((p) => p.paiementStatus === '0')
                            .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0) * 0.2
                        )}
                      </p>
                      <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
                        Commission (20%)
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className="alert alert-warning py-2 px-3 d-flex align-items-start gap-2 mb-0"
                  style={{ fontSize: 12 }}
                >
                  <i className="bi bi-exclamation-triangle-fill flex-shrink-0 mt-1"></i>
                  <span>
                    Les hôtes sans <strong>compte Stripe Connect</strong> ou sans{' '}
                    <strong>PaymentIntentId</strong> seront automatiquement ignorés.
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 pb-4 d-flex gap-2 justify-content-end">
                <button
                  className="btn btn-outline-secondary rounded-pill px-4"
                  onClick={() => setShowBatchModal(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-success rounded-pill px-4 d-flex align-items-center gap-2"
                  onClick={confirmBatchPayout}
                >
                  <i className="bi bi-send-fill"></i>
                  Confirmer les virements
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar mobile */}
      <div
        className="offcanvas offcanvas-start d-md-none"
        tabIndex="-1"
        id="adminSidebarMobile"
        style={{ maxWidth: 280 }}
      >
        <div className="offcanvas-header border-bottom">
          <h6 className="offcanvas-title fw-bold">Administration</h6>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas" />
        </div>
        <div className="offcanvas-body p-3">
          <SideBarAdmin />
        </div>
      </div>
    </>
  );
}

export default AdminPaiements;
